import axiosClient from './axiosClient';
import { MyPermissions, RoleDefinition, UserProfile, UserRole } from '../types';

// --- User Profile API Calls ---

export const fetchUserProfileApi = async (_id: string): Promise<UserProfile | null> => {
  try {
    const response = await axiosClient.get(`/users/${_id}`);
    return response.data;
  } catch (error: any) {
    if (error.response && error.response.status === 404) {
      return null;
    }
    console.error("Error fetching user profile:", error);
    throw error;
  }
};

export const createUserProfileApi = async (_id: string, profileData: Partial<UserProfile>): Promise<UserProfile> => {
  try {
    const response = await axiosClient.post('/users', { ...profileData, _id });
    return response.data;
  } catch (error) {
    console.error("Error creating user profile:", error);
    throw error;
  }
};

export const updateUserProfileApi = async (_id: string, profileUpdate: Partial<UserProfile>): Promise<UserProfile> => {
  try {
    const response = await axiosClient.put(`/users/${_id}`, profileUpdate);
    return response.data;
  } catch (error) {
    console.error("Error updating user profile:", error);
    throw error;
  }
};

export const updateLastActiveApi = async (_id: string): Promise<void> => {
  try {
    await axiosClient.put(`/users/${_id}/last-active`);
  } catch (error) {
    console.error("Error updating last active time:", error);
    throw error;
  }
};

export const updateUserInterestsApi = async (_id: string, interests: string[]): Promise<UserProfile> => {
  try {
    const response = await axiosClient.patch(`/users/${_id}/interests`, { interests });
    return response.data;
  } catch (error) {
    console.error("Error updating user interests:", error);
    throw error;
  }
};

/**
 * Role and suspension are privileged and live on their own admin-only routes.
 *
 * They used to ride along in `PATCH /users/:id`, which is guarded by
 * "self or admin" — so any learner could promote themselves by PATCHing their
 * own record. That endpoint now writes a profile-field allow-list and rejects
 * anything else, these two included.
 *
 * (This call also sent `{ userRole }`, a field no schema has ever had, so the
 * role it wrote was silently dropped even before the allow-list.)
 */
/**
 * Sends `roles` as an array — a person may sit in more than one team.
 * The endpoint still accepts a bare `role` string from older callers, but new
 * code should not rely on that.
 */
export const updateUserRolesApi = async (
  userId: string,
  roles: string[]
): Promise<UserProfile> => {
  const response = await axiosClient.patch(`/users/${userId}/role`, { roles });
  return response.data;
};

export const updateUserRoleApi = async (userId: string, role: UserRole): Promise<UserProfile> =>
  updateUserRolesApi(userId, [role]);

/** What the signed-in user may do. Drives every gate in the admin panel. */
export const fetchMyPermissionsApi = async (): Promise<MyPermissions> => {
  const response = await axiosClient.get('/users/me/permissions');
  return response.data;
};

/** The assignable-role catalogue, for the role picker. */
export const fetchRoleCatalogueApi = async (): Promise<{ roles: RoleDefinition[] }> => {
  const response = await axiosClient.get('/users/roles');
  return response.data;
};

export const updateUserSuspensionApi = async (userId: string, suspended: boolean): Promise<UserProfile> => {
  const response = await axiosClient.patch(`/users/${userId}/suspension`, { suspended });
  return response.data;
};

export const changeUserRoleApi = async (userId: string, newRole: UserRole): Promise<boolean> => {
  try {
    await updateUserRoleApi(userId, newRole);
    return true;
  } catch (error) {
    console.error("Error changing user role:", error);
    throw error;
  }
};

export interface UserRecipient {
  _id: string;
  displayName?: string;
  email?: string;
  photoURL?: string;
  role?: string;
}

/**
 * Every user, reduced to what a recipient picker needs.
 *
 * The Notify page used `getUsers()` — server default `limit: 20` — and then
 * offered "Select All (20 users)" as though that were the platform.
 */
export const getUserRecipients = async (search?: string): Promise<UserRecipient[]> => {
  const response = await axiosClient.get('/users/recipients', {
    params: search ? { search } : undefined,
  });
  return response.data.users;
};

export interface UserFilter {
  page?: number;
  limit?: number;
  searchTerm?: string;
  roleFilter?: string;
}

export const getUsers = async (
  filter: UserFilter = {}
): Promise<{ users: UserProfile[]; totalUsers: number; totalPages: number; currentPage: number }> => {
  try {
    const response = await axiosClient.get('/users', { params: filter });
    return response.data;
  } catch (error) {
    console.error("Error getting all users:", error);
    throw error;
  }
};

export const getAuthorDetails = async (userId: string): Promise<UserProfile> => {
  try {
    const response = await axiosClient.get(`/users/${userId}/author-details`);
    return response.data;
  } catch (error) {
    console.error("Error fetching author details:", error);
    throw error;
  }
};

export const fetchUserGroupReadStatus = async (userId: string, groupId: string): Promise<{ lastRead: Date | null }> => {
  try {
    const response = await axiosClient.get(`/users/${userId}/readStatus/${groupId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching user ${userId} read status for group ${groupId}:`, error);
    throw error;
  }
};

export const updateUserGroupReadStatus = async (userId: string, groupId: string, timestamp: Date): Promise<{ success: boolean }> => {
  try {
    const response = await axiosClient.patch(`/users/${userId}/readStatus/${groupId}`, { timestamp });
    return response.data;
  } catch (error) {
    console.error(`Error updating user ${userId} read status for group ${groupId}:`, error);
    throw error;
  }
};

export const recordStudyTimeApi = async (userId: string, minutes: number): Promise<{ learningStats: any }> => {
  try {
    const response = await axiosClient.post(`/users/${userId}/study-time`, { minutes });
    return response.data;
  } catch (error) {
    console.error("Error recording study time:", error);
    throw error;
  }
};

export { fetchUserProfileApi as getUserProfileById };
export { updateUserProfileApi as updateUser };
